import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Divider,
  Avatar,
  Stack,
  IconButton,
  Chip,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { get, post } from '../../utility/fetch';
import { get as gets } from '../../utility/fetchWeb';
import '../../Assets/articlestyle.css';

import defaultImage from '../../Assets/images/image2.png';
import Tooltip from '@mui/material/Tooltip';
import UploadButton from '../Layout/UploadButton';
import notification from '../../utility/notification';
import CloseIcon from '@mui/icons-material/Close';
import DOMPurify from 'dompurify';

const ArticleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentImage, setCommentImage] = useState(null); // State for uploaded image
  const [loadingComments, setLoadingComments] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [showCommentSection, setShowCommentSection] = useState(false);

  // Pagination states for comments
  const [commentPage, setCommentPage] = useState(1);
  const [commentPageSize] = useState(10);
  const [totalComments, setTotalComments] = useState(0);
  const [hasMoreComments, setHasMoreComments] = useState(false);

  const [deleteCommentId, setDeleteCommentId] = useState(null); // State for comment to delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // State for confirmation modal

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchArticle = async () => {
      // Validate article ID
      console.log('Fetching article with ID:', id); // Debug log
      if (!id || isNaN(parseInt(id))) {
        setError('Invalid article ID');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await gets(`/articles/${id}`);
        console.log('Article response:', response); // Debug log

        if (response?.data) {
          const articleData = response.data;

          // Fetch author details based on userType
          if (articleData.userId && articleData.userType) {
            try {
              let authorResponse;
              if (articleData.userType === 1) {
                // Fetch patient details
                authorResponse = await gets(`/patient/single/${articleData.userId}`);
              } else if (articleData.userType === 2) {
                // Fetch employee details
                authorResponse = await get(`/employee/employee/${articleData.userId}`);
              }

              if (authorResponse?.data) {
                const authorData = authorResponse.data;
                articleData.authorName = `${authorData.firstName || ''} ${authorData.lastName || ''}`.trim() || 'Anonymous';
                articleData.authorPictureUrl = authorData.profilePicture || '';
              } else {
                articleData.authorName = 'Anonymous';
                articleData.authorPictureUrl = '';
              }
            } catch (authorError) {
              console.error('Error fetching author details:', authorError);
              articleData.authorName = 'Anonymous';
              articleData.authorPictureUrl = '';
            }
          } else {
            articleData.authorName = 'Anonymous';
            articleData.authorPictureUrl = '';
          }

          setArticle(articleData);
        } else {
          console.log('No data in response'); // Debug log
          setError('Article not found');
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        setError('Failed to load article. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();

    // Only fetch comments if we have a valid ID
    if (id && !isNaN(parseInt(id))) {
      fetchComments(1);
    }
  }, [id]);

  const fetchComments = async (page = 1, reset = false) => {
    setLoadingComments(true);
    try {
      const response = await get(`/articleComment/list/article/${id}/${page}/${commentPageSize}`);
      if (response?.isSuccess && response?.data?.result) {
        const newComments = response.data.result.map(comment => ({
          id: comment.id,
          commentText: comment.comment,
          commenterFullName: `${comment.employee.firstName} ${comment.employee.lastName}`,
          profilePictureUrl: comment.employee.pictureUrl,
          createdAt: comment.createdAt,
          articleId: comment.articleId,
          commenterId: comment.employee?.id, // Store commenter ID for potential future use 
          pictureUrl: comment.pictureUrl || null, // Include image URL if available
        }));

        if (reset || page === 1) {
          setComments(newComments);
        } else {
          setComments(prev => [...prev, ...newComments]);
        }

        setTotalComments(response.data.totalCount || newComments.length);
        setHasMoreComments(newComments.length === commentPageSize);
        setCommentPage(page);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() && !commentImage) return; // Ensure either a comment or image is present

    setPostingComment(true);
    try {
      const payload = {
        comment: newComment.trim(),
        articleId: parseInt(id),
        pictureUrl: commentImage || null, // Include the uploaded image URL
      };
      const response = await post(`/articleComment/create/employee/${sessionStorage.getItem('userId')}`, payload);
      if (response?.isSuccess) {
        const newCommentObj = {
          id: response.data.articleCommentId,
          commentText: newComment,
          commenterFullName: sessionStorage.getItem('name') || 'You',
          profilePictureUrl: sessionStorage.getItem('profilePicture') || '',
          createdAt: new Date().toISOString(),
          pictureUrl: commentImage ||  null, // Include the image in the new comment
        };
        fetchComments(1, true); // Refresh comments after posting
        setComments(prev => [newCommentObj, ...prev]);
        setNewComment('');
        setCommentImage(null); // Reset image after posting
      } else {
        console.error('Failed to post comment:', response?.error);
      }
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setPostingComment(false);
    }
  };

  const handleUploadSuccess = (response) => {
    if (response?.code === 1) {
      setCommentImage(response.doclink); // Store the uploaded image URL
      // notification({
      //   type: 'success',
      //   message: 'Image uploaded successfully',
      // });
    }
  };

  const handleUploadError = (error) => {
    console.error('Image upload failed:', error);
    notification({
      type: 'error',
      message: 'Failed to upload image',
    });
  };

  const handleLoadMoreComments = () => {
    if (!loadingComments && hasMoreComments) {
      fetchComments(commentPage + 1, false);
    }
  };

  const handleToggleComments = () => {
    setShowCommentSection(!showCommentSection);
    if (!showCommentSection) {
      fetchComments(1, true); // Fetch comments when opening section
    }
  };

  const handleDeleteComment = async () => {
    try {
      await post(`/ArticleComment/${deleteCommentId}`);
      setComments(prev => prev.filter(comment => comment.id !== deleteCommentId)); // Remove comment from state
      notification({ type: 'success', message: 'Comment deleted successfully' });
    } catch (error) {
      console.error('Error deleting comment:', error);
      notification({ type: 'error', message: 'Failed to delete comment' });
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteCommentId(null);
    }
  };

  const openDeleteModal = (commentId) => {
    setDeleteCommentId(commentId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteCommentId(null);
  };

  const ArticleSkeleton = () => (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        borderRadius: { xs: 0, md: 3 },
        bgcolor: 'background.paper',
        width: '100%',
      }}
    >
      {/* Hero Image Skeleton */}
      <Skeleton
        variant="rectangular"
        width="100%"
        height={{ xs: 200, md: 360 }}
        animation="wave"
      />

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Category and Date Skeleton */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: '16px' }} />
          <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: '16px' }} />
        </Stack>

        {/* Title Skeleton */}
        <Skeleton
          variant="text"
          width="80%"
          height={60}
          sx={{ fontSize: { xs: '1.5rem', md: '2.5rem' }, mb: 3 }}
        />

        {/* Author Info Skeleton */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            mb: 4,
            pb: 4,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Skeleton variant="circular" width={56} height={56} />
          <Box>
            <Skeleton variant="text" width={150} height={24} />
            <Skeleton variant="text" width={100} height={20} />
          </Box>
        </Stack>

        {/* Content Skeleton */}
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="95%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="85%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="92%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="88%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="70%" height={24} />
        </Box>

        {/* Comments Section Skeleton */}
        <Box sx={{ mt: 4 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={50}
            sx={{ borderRadius: '4px', mb: 2 }}
          />
        </Box>
      </Box>
    </Paper>
  );

  // Error state component
  const ArticleError = ({ message }) => (
    <Paper sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6" color="error" gutterBottom>
        {message}
      </Typography>
      <Button
        variant="contained"
        onClick={() => navigate('/landing')}
        sx={{ mt: 2 }}
      >
        Back to Articles
      </Button>
    </Paper>
  );

  // Sanitize article full description with DOMPurify
  const rawHtml = article?.fullDescription || '';
  const trimmedHtml = rawHtml.replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '');
  const cleanHtml = DOMPurify.sanitize(trimmedHtml, {
    USE_PROFILES: { html: true }
  });
  

  console.log('Clean HTML:', cleanHtml); // Debug log to check sanitized HTML

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        width: '100vw',
        overflow: 'hidden',
      }}
    >
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          py: { xs: 2, md: 4 },
          px: { xs: 2, md: 4 },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 4,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'background.default',
            py: 2,
            width: '100%',
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.100' },
              mr: 2,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="h1" noWrap>
            Article Details
          </Typography>
        </Box>

        {/* Show skeleton while loading, error state if error, article content when loaded */}
        {loading ? (
          <ArticleSkeleton />
        ) : error ? (
          <ArticleError message={error} />
        ) : article ? (
          <Paper
            elevation={0}
            sx={{
              overflow: 'hidden',
              borderRadius: { xs: 0, md: 3 },
              bgcolor: 'background.paper',
              transition: 'all 0.3s ease-in-out',
              width: '100%',
            }}
          >
            {/* Hero Image Section */}
            <Box sx={{ position: 'relative', bgcolor: 'grey.100' }}>
              <Box
                component="img"
                src={
                  article.featuredImagePath
                    ? `${process.env.REACT_APP_MEDIA_URL}${article.featuredImagePath}`
                    : defaultImage
                }
                alt={article.title}
                sx={{
                  width: '100%',
                  height: { xs: 200, md: 360 }, // Reduced height by 20%
                  objectFit: 'cover',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
              />
            </Box>

            <Box sx={{ p: { xs: 2, md: 4 } }}>
              {/* Category and Date */}
              <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
                <Chip
                  icon={<CategoryIcon />}
                  label={article.category?.name}
                  color="primary"
                  size="small"
                />
                <Chip
                  icon={<AccessTimeIcon />}
                  label={new Date(article.publishedDate).toLocaleDateString()}
                  variant="outlined"
                  size="small"
                />
              </Stack>

              {/* Title */}
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '2.5rem' },
                  mb: 3,
                }}
              >
                {article.title}
              </Typography>

              {/* Author Info */}
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{
                  mb: 4,
                  pb: 4,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Avatar
                  src={article.authorPictureUrl}
                  sx={{ width: 56, height: 56 }}
                />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {article.authorName || 'Anonymous'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {article.category?.name || 'General'}
                  </Typography>
                </Box>
              </Stack>

              {/* Short Description
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 4,
                  fontSize: '1.1rem',
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  borderLeft: 4,
                  borderColor: 'primary.main',
                  pl: 2
                }}
              >
                {article.shortDescription}
              </Typography> */}

              {/* Full Description */}
              <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: cleanHtml }}
              />
              {/* Comments Section - Updated with SocialFeed Design */}
              <Box sx={{ mt: 4 }}>
                {/* Comment Button Bar */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  alignItems: 'center',
                  py: 1,
                  borderTop: '1px solid #eee',
                  borderBottom: '1px solid #eee',
                  mb: 2
                }}>
                  <Tooltip title="Comments" arrow>
                    <Button
                      color="inherit"
                      startIcon={<ChatBubbleOutlineIcon />}
                      onClick={handleToggleComments}
                    >
                      {totalComments || 0}
                    </Button>
                  </Tooltip>
                </Box>

                {/* Comment Input Section - Same as SocialFeed */}
                {showCommentSection && (
                  <Box sx={{ mb: 2 }}>
                    {/* Uploaded Image Preview - Displayed Above the Comment Field */}
                    {commentImage && (
                      <Box
                        sx={{
                          position: 'relative',
                          mb: 2,
                          width: '100%',
                          maxWidth: 200,
                          borderRadius: '12px',
                          overflow: 'hidden',
                        }}
                      >
                        <img
                          src={commentImage}
                          alt="Uploaded"
                          style={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: '12px',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => setCommentImage(null)} // Remove image
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}

                    {/* Comment Input Section */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Avatar
                        src={sessionStorage.getItem('profilePicture')}
                        sx={{ width: 40, height: 40, mr: 2 }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          variant="outlined"
                          placeholder="What's on your mind?"
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          sx={{
                            backgroundColor: '#f5f5f5',
                            borderRadius: '12px',
                            '& .MuiOutlinedInput-root': {
                              padding: '12px',
                            },
                          }}
                        />
                      </Box>
                      <UploadButton
                        type="comment" // Pass "comment" type to display camera icon
                        onUploadSuccess={handleUploadSuccess}
                        onUploadError={handleUploadError}
                        patientRef={fileInputRef}
                      />
                    </Box>

                    {/* Publish Button */}
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleAddComment}
                      disabled={postingComment || (!newComment.trim() && !commentImage)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '12px',
                        fontWeight: 'bold',
                        mt: 1,
                      }}
                    >
                      {postingComment ? <CircularProgress size={20} /> : 'Post'}
                    </Button>
                  </Box>
                )}

                {/* Display Comments with Pagination */}
                {showCommentSection && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    {comments.length > 0 ? (
                      <>
                        {comments.map((comment) => (
                          <Card key={comment.id} sx={{ mb: 2, position: 'relative', '&:hover .delete-icon': { display: 'block' } }}>
                            <CardContent>
                              {/* Comment Header */}
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar src={comment.profilePictureUrl} alt={comment.commenterFullName} sx={{ width: 40, height: 40, mr: 2 }} />
                                <Box>
                                  <Typography variant="subtitle1" fontWeight="bold">{comment.commenterFullName}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {comment.createdAt ? new Date(comment.createdAt).toLocaleDateString() : 'Just now'}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Comment Text */}
                              <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
                                {comment.commentText}
                              </Typography>

                              {/* Comment Media */}
                              {comment.pictureUrl && (
                                <Box sx={{ position: 'relative', mb: 2, borderRadius: '8px', overflow: 'hidden' }}>
                                  <img
                                    src={comment.pictureUrl}
                                    alt="Comment Media"
                                    style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
                                  />
                                </Box>
                              )}

                              {/* Delete Icon (Visible only to owner) */}
                              {sessionStorage.getItem('userId') === String(comment.commenterId) && (
                                <IconButton
                                  className="delete-icon"
                                  onClick={() => openDeleteModal(comment.id)}
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    display: 'none',
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </CardContent>
                          </Card>
                        ))}

                        {/* Load More Comments Button */}
                        {hasMoreComments && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button
                              variant="outlined"
                              onClick={handleLoadMoreComments}
                              disabled={loadingComments}
                              startIcon={loadingComments ? <CircularProgress size={16} /> : <KeyboardArrowDownIcon />}
                              sx={{
                                textTransform: 'none',
                                borderRadius: '20px'
                              }}
                            >
                              {loadingComments ? 'Loading...' : 'Load More Comments'}
                            </Button>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No comments yet. Be the first to comment!
                      </Typography>
                    )}

                    {/* Loading indicator for initial load */}
                    {loadingComments && comments.length === 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        ) : (
          <ArticleError message="Article not found" />
        )}

        {/* Confirmation Modal for Deleting Comment */}
        <Dialog open={isDeleteModalOpen} onClose={closeDeleteModal}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this comment? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteModal}>Cancel</Button>
            <Button onClick={handleDeleteComment} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      
    </Box>
    
  );

  
};

export default ArticleDetails;